import axios from 'axios'
import { useAuthStore } from '@/stores/auth'

// Environment-based configuration
const getBaseURL = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }
  
  // Fallback for development
  if (import.meta.env.DEV) {
    return 'http://localhost:8001/api'
    //return 'https://e0e5fc46d201.ngrok-free.app/api'
  }
  
  // Production fallback - assumes API is on same domain 
  return 'https://kijanipantry.com'
}

export const getImageBaseURL = () => {
  if (import.meta.env.VITE_IMAGE_URL) {
    return import.meta.env.VITE_IMAGE_URL
  }
  
  // Fallback for development
  if (import.meta.env.DEV) {
    return 'http://localhost:8001'
     //return 'https://e0e5fc46d201.ngrok-free.app'
  }
  
  // Production fallback - assumes images are on same domain
  return 'https://kijanipantry.com'
}

const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
})

// Development mode configuration
const isDevelopment = import.meta.env.DEV || process.env.NODE_ENV === 'development'
const hasDevEndpoints = import.meta.env.VITE_HAS_DEV_ENDPOINTS === 'true'

api.interceptors.request.use((config) => {
  const authStore = useAuthStore()
  
  // Route to dev endpoints ONLY for unauthenticated users when dev endpoints are available
  if (hasDevEndpoints && config.url && !authStore.token) {
    // Map protected routes to development routes for unauthenticated users only
    const devRoutes: Record<string, string> = {
      '/categories': '/dev/categories',
      '/products': '/dev/products',
      '/inventory/low-stock': '/dev/inventory/low-stock',
      '/dashboard/metrics': '/dev/dashboard/metrics',
      '/dashboard/inventory-status': '/dev/dashboard/inventory-status',
      '/dashboard/sales-trends': '/dev/dashboard/sales-trends',
      '/dashboard/top-products': '/dev/dashboard/top-products',
      '/dashboard/recent-orders': '/dev/dashboard/recent-orders',
      '/dashboard/performance-kpis': '/dev/dashboard/performance-kpis',
      '/dashboard/stock-movement-trends': '/dev/dashboard/stock-movement-trends'
    }
    
    // Only use dev routes for non-auth endpoints when NOT authenticated
    if (devRoutes[config.url] && !config.url.startsWith('/auth')) {
      config.url = devRoutes[config.url]
    }
  }
  
  // Note: baseURL already includes /api, so no need to prepend it here
  
  if (authStore.token) {
    config.headers.Authorization = `Bearer ${authStore.token}`
  }
  return config
})

api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const authStore = useAuthStore()
    const originalRequest = error.config
    
    // Handle 401 errors
    if (error.response?.status === 401) {
      // If this is a failed refresh request, logout immediately
      if (originalRequest.url?.includes('/refresh')) {
        authStore.logout()
        return Promise.reject(error)
      }
      
      // Don't retry if already attempted
      if (originalRequest._retry) {
        authStore.logout()
        return Promise.reject(error)
      }
      
      // Try to refresh token only once
      originalRequest._retry = true
      
      try {
        const refreshed = await authStore.refreshToken()
        
        if (refreshed && authStore.token) {
          // Retry the original request with the new token
          originalRequest.headers.Authorization = `Bearer ${authStore.token}`
          return api(originalRequest)
        } else {
          authStore.logout()
        }
      } catch (refreshError) {
        authStore.logout()
      }
    }
    
    return Promise.reject(error)
  }
)

export default api